import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import ModelSelector from "../components/ModelSelector";
import { MODEL_CONFIG } from "../config/models";
import "./VolcanesPage.css";

const DEFAULT_KEY = "modelo1";

function parseVec(str, fallback) {
  const parts = str.split(" ").map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return fallback;
  return new THREE.Vector3(parts[0], parts[1], parts[2]);
}

export default function VolcanesPage() {
  const mountRef = useRef(null);
  const [selected, setSelected] = useState(DEFAULT_KEY);
  const [sceneReady, setSceneReady] = useState(false);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const modelGroupRef = useRef(null);
  const frameRef = useRef(null);

  const initScene = useCallback(() => {
    const mount = mountRef.current;
    if (!mount || mount.clientWidth === 0) return;

    const w = mount.clientWidth;
    const h = mount.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x120808);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    camera.position.set(0, 0.4, 1.2);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 0.9);
    dir.position.set(2, 4, 3);
    scene.add(dir);

    const group = new THREE.Group();
    scene.add(group);
    modelGroupRef.current = group;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controlsRef.current = controls;

    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();
  }, []);

  const disposeScene = useCallback(() => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    const renderer = rendererRef.current;
    const mount = mountRef.current;
    if (renderer && mount && renderer.domElement.parentNode === mount) {
      mount.removeChild(renderer.domElement);
    }
    rendererRef.current?.dispose();
    rendererRef.current = null;
    sceneRef.current = null;
    cameraRef.current = null;
    controlsRef.current = null;
    modelGroupRef.current = null;
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      initScene();
      setSceneReady(true);
    });
    const onResize = () => {
      const mount = mountRef.current;
      const camera = cameraRef.current;
      const renderer = rendererRef.current;
      if (!mount || !camera || !renderer) return;
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", onResize);
      setSceneReady(false);
      disposeScene();
    };
  }, [initScene, disposeScene]);

  useEffect(() => {
    if (!sceneReady) return undefined;
    const group = modelGroupRef.current;
    if (!group) return undefined;

    while (group.children.length) {
      const o = group.children[0];
      group.remove(o);
      o.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          const mats = Array.isArray(child.material)
            ? child.material
            : [child.material];
          mats.forEach((m) => m.dispose?.());
        }
      });
    }

    const cfg = MODEL_CONFIG[selected];
    if (!cfg) return undefined;

    const loader = new GLTFLoader();
    let cancelled = false;

    loader.load(
      cfg.url,
      (gltf) => {
        if (cancelled) return;
        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);
        const scale = parseVec(cfg.scale, new THREE.Vector3(1, 1, 1));
        model.scale.set(scale.x, scale.y, scale.z);
        const pos = parseVec(cfg.position, new THREE.Vector3(0, 0, 0));
        model.position.add(pos);
        group.add(model);
      },
      undefined,
      (err) => console.error("Error cargando modelo:", err)
    );

    return () => {
      cancelled = true;
    };
  }, [selected, sceneReady]);

  return (
    <div className="volcanes-page">
      <ModelSelector
        selected={selected}
        onSelect={setSelected}
        backSlot={
          <div className="volcanes-top-links">
            <Link to="/" className="volcanes-back-link">
              ← Inicio
            </Link>
            <Link to="/mapa" className="volcanes-back-link">
              Mapa Chile
            </Link>
          </div>
        }
      />
      <div className="volcanes-viewport" ref={mountRef} />
      <p className="volcanes-hint">
        Arrastra para rotar · Rueda o pellizco para zoom
      </p>
    </div>
  );
}
